import { Injectable, inject, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { API_CONFIG } from 'app/api-config/api-config';
import { IApiConfig, IProgramSummaryResponse, IInfo, IQuestionnaires, IHeader, IAssessmentsUserProfile } from 'app/interfaces';
import { UserSessionsService } from '../../../shared/services/user-sessions/user-sessions.service';
import { PROGRAM_SUMMARY_MAP } from '../../../shared/constants/client-constants';
import { Router } from '@angular/router';
import { FLU_CAMPAIGN_ID, COVID_CAMPAIGN_ID } from '../../../shared/constants/campaign-constants';
import { AssessmentsService } from '../../assessments/services/assessments.service';
@Injectable()
export class HomeService implements OnDestroy {
  public activeCampaign: BehaviorSubject<string> = new BehaviorSubject('covid');
  public fluHeader: BehaviorSubject<IHeader | null> = new BehaviorSubject<IHeader | null>(null);
  public covidHeader: BehaviorSubject<IHeader | null> = new BehaviorSubject<IHeader | null>(null);
  public covidInfo: BehaviorSubject<IInfo[]> = new BehaviorSubject<IInfo[]>([]);
  public fluInfo: BehaviorSubject<IInfo[]> = new BehaviorSubject<IInfo[]>([]);
  public covidQuestionnaires: BehaviorSubject<IQuestionnaires[]> = new BehaviorSubject<IQuestionnaires[]>([]);
  public fluQuestionnaires: BehaviorSubject<IQuestionnaires[]> = new BehaviorSubject<IQuestionnaires[]>([]);
  public userProfile: Subject<string> = new Subject();
  private campaignMap: Array<string> = ['flu', 'covid'];
  private config: IApiConfig = inject(API_CONFIG);
  private http: HttpClient = inject(HttpClient);
  private router: Router = inject(Router);
  private userSessionsService: UserSessionsService = inject(UserSessionsService);
  private destroy$ = new Subject<void>();
  assessmentsService = inject(AssessmentsService);
  constructor() {
    if (!localStorage.getItem('activeCampaign')) {
      this.setActiveCampaign('flu');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setActiveCampaign(campaign: string) {
    localStorage.setItem('activeCampaign', campaign);
    this.activeCampaign.next(campaign);
  }

  getActiveCampaign() {
    const activeCampaign = localStorage.getItem('activeCampaign');
    return activeCampaign === this.campaignMap[1] ? COVID_CAMPAIGN_ID : FLU_CAMPAIGN_ID;
  }

  fetchProgramSummary(selectedCapmaign: number) {
    const params = new HttpParams().set('employeeId', this.userSessionsService.employeeId.getValue()).set('campaignId', selectedCapmaign);

    this.http
      .get(`${this.config?.basicApi}${this.config.programSummary}`, { params: params })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response) {
            this.setResponse(response as IProgramSummaryResponse, selectedCapmaign);
          }
        },
        error: (error) => {
          if (error['offlineMode']) {
            this.setResponse(PROGRAM_SUMMARY_MAP as IProgramSummaryResponse, selectedCapmaign);
          }
        },
      });
  }

  getAssessmentToken(data: IAssessmentsUserProfile) {
    return this.http.post<{ token: string }>(`${this.config?.basicApi}${this.config.userProfile}`, data);
  }

  getUserProfile(token: string) {
    const headers = new HttpHeaders().append('assessments-token', token);
    const params = new HttpParams().set('token', token);
    return this.http.get<{ userProfile: IAssessmentsUserProfile }>(`${this.config?.basicApi}${this.config.userProfile}`, {
      params: params,
      headers: headers,
    });
  }

  updateProgramSummary(assessmentInstanceId: number | undefined, assessmentId: number | null) {
    const data = {
      assessmentInstanceId: assessmentInstanceId,
      assessmentId: assessmentId,
      campaignId: this.getActiveCampaign(),
    };
    return this.http.post<{ id: number }>(`${this.config?.basicApi}${this.config.questionnaireInstances}`, data);
  }

  setResponse(resp: IProgramSummaryResponse, selectedCapmaign: number) {
    if (this.getActiveCampaign() == selectedCapmaign) {
      const info: Array<IInfo> = [];
      const questionnaires: Array<IQuestionnaires> = [];

      resp['info'].forEach((item) => {
        info.push({
          name: item['name'],
          description: item['description'],
          statusId: item['statusId'],
          detailsTitle: item['detailsTitle'],
          detailsDescription: item['detailsDescription'],
          imageUrl: item['imageUrl'],
          receiptTypeId: item['receiptTypeId'],
          isTaken: item['isTaken'],
          documentUploadMessage: item['documentUploadMessage'],
          receipts: item['receipts'],
        });
      });
      resp['questionnaires'].forEach((item) => {
        questionnaires.push({
          documentId: item['documentId'],
          cloudDocumentUri: item['cloudDocumentUri'],
          assessmentId: item['assessmentId'],
          canEmployeeUploadDocuments: item['canEmployeeUploadDocuments'],
          name: item['name'],
          title: item['name'],
          description: item['description'],
          statusId: item['statusId'],
          isTaken: item['isTaken'],
          typeId: item['typeId'],
          receiptTypeId: item['receiptTypeId'],
          questionnaireInstanceId: item['questionnaireInstanceId'],
          documentUploadMessage: item['documentUploadMessage'],
          id: item['id'],
          receipts: item['receipts'],
        });
      });
      if (selectedCapmaign == FLU_CAMPAIGN_ID) {
        this.fluQuestionnaires.next(questionnaires);
        this.fluInfo.next(info);
        this.fluHeader.next({
          title: resp['moduleTitle'],
          description: resp['moduleDescription'],
          message: resp['statusMessage'],
          statusId: resp['statusId'],
        });
      } else {
        this.covidQuestionnaires.next(questionnaires);
        this.covidInfo.next(info);
        this.covidHeader.next({
          title: resp['moduleTitle'],
          description: resp['moduleDescription'],
          message: resp['statusMessage'],
          statusId: resp['statusId'],
        });
      }
    }
  }

  startAssessment(question: IQuestionnaires) {
    localStorage.setItem('allowAssessment', 'true');

    const postAssessmentRedirectUrl = question.canEmployeeUploadDocuments ? '/home/documents' : '/home';
    localStorage.setItem('postAssessmentRedirectUrl', postAssessmentRedirectUrl);
    this.getAssessmentToken({
      assessmentId: question.assessmentId,
      type: 'FLU_APP',
      sourceKey: 'flu-app',
      externalId: this.userSessionsService.employeeId.getValue(),
      eventType: 'FLU_APP_ASSESSMENT_END_EVENT',
      screenType: 'QUESTIONS_SCREEN',
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp) => {
          if (resp) {
            this.assessmentsService.setAssessmentsRequest({
              assessmentsToken: resp.token,
              progressBarTitle: this.activeCampaign.value === 'flu' ? 'Flu Questionnaire' : 'COVID-19 Questionnaire',
              summaryTitle: 'Summary',
              summaryDescription: 'Summary of questionnaire responses',
              redirectUrl: '/post-assessment',
            });
            this.router.navigate(['assessments']);
          }
        },
      });
  }
}
